import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SketchPicker, ColorResult } from "react-color";
import { formatDistanceStrict } from "date-fns";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useNotification } from "../../../../context/NotificationContext";
import {
    getHohoInfo,
    saveHohoInfo,
    updateHohoVisibility,
    getBusesForTrack,
    affiliateBus,
    unaffiliateBus,
} from "../../../../api";
import NumberInput from "../../../../components/common/NumberInput";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import { BusForAffiliation } from "../../../../types/types";
import { getDateFnsLocale } from "../../../../utils/dateFnsLocale";
import { ReactComponent as RefreshIcon } from "../../../../assets/images/refresh-line.svg";
import Button from "../../../../components/ui/Button";
import stepStyles from "../createStep.module.css";
import studio from "../../../../components/modals/StudioModal.module.css";
import styles from "./Tracking.module.css";

interface BusTrackingProps {
    navigateToNext?: () => void;
}

// Same threshold as rewind-application-rest (getBusesLocationTrack.js): past it, the app hides the bus.
const BUS_STALE_AFTER_MS = 15 * 60 * 1000;

const BusTracking: React.FC<BusTrackingProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { showNotification } = useNotification();
    // frequency is stored as a string to match the input value (empty string if unset)
    const [frequency, setFrequency] = useState<string | null>(null);
    // display indicates if bus tracking is active
    const [display, setDisplay] = useState<boolean>(false);
    const [busColor, setBusColor] = useState<string>("#FF6F61"); // Default color
    const [busLineColor, setBusLineColor] = useState<string>("#FF6F61"); // Default color
    const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
    const [isPickerLineOpen, setIsPickerLineOpen] = useState<boolean>(false);
    const [affiliatedBuses, setAffiliatedBuses] = useState<BusForAffiliation[]>([]);
    const [availableBuses, setAvailableBuses] = useState<BusForAffiliation[]>([]);
    // Bus pending a transfer confirmation (already linked to another tour).
    const [busToTransfer, setBusToTransfer] = useState<BusForAffiliation | null>(null);
    // When the bus list (and thus the "last position" column) was last fetched.
    const [busesFetchedAt, setBusesFetchedAt] = useState<Date | null>(null);
    const [isRefreshingBuses, setIsRefreshingBuses] = useState<boolean>(false);
    // Guards the config auto-save so we never persist the default (empty) state
    // before the real configuration has been loaded from the backend.
    const hasLoaded = useRef<boolean>(false);
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;

    const handleColorChange = (selectedColor: ColorResult) => {
        setBusColor(selectedColor.hex);
    };

    const handleLineColorChange = (selectedColor: ColorResult) => {
        setBusLineColor(selectedColor.hex);
    };

    const toggleColorPicker = () => {
        setIsPickerLineOpen(false);
        setIsPickerOpen((prev) => !prev);
    };

    const toggleLineColorPicker = () => {
        setIsPickerOpen(false);
        setIsPickerLineOpen((prev) => !prev);
    };

    const getHohoInfoAPI = async (trackId: string) => {
        const token = localStorage.getItem("token");

        if (!token || !trackId) return;

        const ret = await getHohoInfo({ token, trackId });
        if (ret && ret.track) {
            setDisplay(ret.track.isBusTracking ?? false);
            setFrequency(
                ret.track.frequency !== undefined && ret.track.frequency !== null ? String(ret.track.frequency) : null,
            );
            setBusColor(ret.track.busColor || "#FF6F61");
            setBusLineColor(ret.track.busLineColor || "#FF6F61");
        }
        // Only allow auto-saving once the real config has been loaded.
        hasLoaded.current = true;
    };

    const getBusesAPI = async (trackId: string) => {
        const token = localStorage.getItem("token");
        if (!token || !trackId) return;

        const ret = await getBusesForTrack({ token, trackId });
        if (ret) {
            setAffiliatedBuses(ret.affiliated || []);
            setAvailableBuses(ret.available || []);
            setBusesFetchedAt(new Date());
        }
    };

    const handleRefreshBuses = async () => {
        if (!trackData.trackId || isRefreshingBuses) return;
        setIsRefreshingBuses(true);
        try {
            await getBusesAPI(trackData.trackId);
        } finally {
            setIsRefreshingBuses(false);
        }
    };

    const saveHohoInfoAPI = async (overrideDisplay?: boolean) => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        const frequencyInt = frequency ? parseInt(frequency) : 0;
        if (!token || !trackId) return;
        await saveHohoInfo({
            token,
            trackId,
            busColor,
            busLineColor,
            frequency: frequencyInt,
            isDisplay: overrideDisplay !== undefined ? overrideDisplay : display,
        });
    };

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getHohoInfoAPI(trackData.trackId);
            getBusesAPI(trackData.trackId);
        }
        // Reset the load guard when switching tracks so we re-fetch cleanly.
        return () => {
            hasLoaded.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId]);

    const renderLastPosition = (bus: BusForAffiliation) => {
        const date = bus.lastPositionAt ? new Date(bus.lastPositionAt) : null;

        // An invalid date would make date-fns throw and crash the whole step, so treat it as "never".
        if (!date || Number.isNaN(date.getTime())) {
            return (
                <span className={`${styles.posBadge} ${styles.posNever}`} title={t("lastPositionNeverHint")}>
                    {t("lastPositionNever")}
                </span>
            );
        }

        // Relative to the last fetch (not "now"), so the label matches the "last updated" time shown above.
        const reference = busesFetchedAt ?? new Date();
        // Guard against a browser clock slightly behind the server ("in 2 seconds")
        const elapsedFrom = date > reference ? reference : date;
        const isStale = reference.getTime() - date.getTime() >= BUS_STALE_AFTER_MS;
        const exactDate = date.toLocaleString(i18n.language);

        return (
            <span
                className={`${styles.posBadge} ${isStale ? styles.posStale : styles.posLive}`}
                title={isStale ? `${exactDate} — ${t("lastPositionStaleHint")}` : exactDate}
            >
                {formatDistanceStrict(elapsedFrom, reference, {
                    addSuffix: true,
                    locale: getDateFnsLocale(i18n.language),
                })}
            </span>
        );
    };

    // Toggling the "Activate" switch is an explicit user action: update the
    // local state AND persist immediately with the new value. This avoids the
    // previous race where an effect saved the default `false` on mount and
    // silently disabled tracking.
    const handleToggleTracking = async (isChecked: boolean) => {
        setDisplay(isChecked);
        if (!hasLoaded.current) return;
        await saveHohoInfoAPI(isChecked);
    };

    const handleContinue = async () => {
        try {
            await saveHohoInfoAPI();
            showNotification({
                message: t("busTrackingSaved") || "Bus tracking configuration saved",
                type: "success",
            });
            if (navigateToNext) {
                navigateToNext();
            }
        } catch (error) {
            showNotification({
                message: t("somethingWentWrong") || "Something went wrong. Please try again.",
                type: "error",
            });
        }
    };

    const toggleDisplayMap = async (id: string, currentValue: boolean) => {
        try {
            const token = localStorage.getItem("token");
            const trackId = trackData.trackId;
            if (!token || !trackId) return;

            await updateHohoVisibility({ token, id, displayMap: !currentValue });
            await getBusesAPI(trackId);
        } catch (error) {
            console.error("Error updating displayMap:", error);
        }
    };

    const affiliateBusById = async (busId: string) => {
        try {
            const token = localStorage.getItem("token");
            const trackId = trackData.trackId;
            if (!token || !trackId) return;

            await affiliateBus({ token, trackId, busId });
            await getBusesAPI(trackId);
            showNotification({
                message: t("busAffiliated") || "Bus affiliated to this tour",
                type: "success",
            });
        } catch (error) {
            showNotification({
                message: t("somethingWentWrong") || "Something went wrong. Please try again.",
                type: "error",
            });
        }
    };

    // A bus can only be linked to one tour at a time. If it already belongs to
    // another tour, ask for confirmation so the user understands it will be
    // moved (and thus disappear from its previous tour).
    const handleAffiliateClick = (bus: BusForAffiliation) => {
        if (bus.currentTrackId) {
            setBusToTransfer(bus);
        } else {
            affiliateBusById(bus.id);
        }
    };

    const confirmTransfer = async () => {
        if (!busToTransfer) return;
        const busId = busToTransfer.id;
        setBusToTransfer(null);
        await affiliateBusById(busId);
    };

    const handleUnaffiliate = async (busId: string) => {
        try {
            const token = localStorage.getItem("token");
            const trackId = trackData.trackId;
            if (!token || !trackId) return;

            await unaffiliateBus({ token, busId });
            await getBusesAPI(trackId);
            showNotification({
                message: t("busUnaffiliated") || "Bus removed from this tour",
                type: "success",
            });
        } catch (error) {
            showNotification({
                message: t("somethingWentWrong") || "Something went wrong. Please try again.",
                type: "error",
            });
        }
    };

    return (
        <div className={`step-container ${styles.root}`}>
            <div className={styles.sec}>
                <div className={styles.secRow}>
                    <TitleSubtitle
                        title={t("realTimeBusTracking")}
                        subtitle={t("adjustTheStopsOfYourTourHere")}
                        layout="busTracking"
                    />
                    <div className={styles.activate}>
                        <span className={styles.activateLabel}>{t("activate")}</span>
                        <ToggleSwitch checked={display} onChange={handleToggleTracking} />
                    </div>
                </div>
            </div>

            {display === true && (
                <>
                    <div className={styles.sec}>
                        <div className={styles.secRow}>
                            <TitleSubtitle title={t("busColor")} subtitle={""} layout="busTracking" />
                            <div className={styles.swatchWrap}>
                                <button
                                    type="button"
                                    className={styles.swatch}
                                    style={{ backgroundColor: busColor }}
                                    onClick={toggleColorPicker}
                                    aria-label={t("busColor")}
                                />
                                {isPickerOpen && (
                                    <div className={styles.picker}>
                                        <SketchPicker color={busColor} onChange={handleColorChange} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.sec}>
                        <div className={styles.secRow}>
                            <TitleSubtitle title={t("busLineColor")} subtitle={""} layout="busTracking" />
                            <div className={styles.swatchWrap}>
                                <button
                                    type="button"
                                    className={styles.swatch}
                                    style={{ backgroundColor: busLineColor }}
                                    onClick={toggleLineColorPicker}
                                    aria-label={t("busLineColor")}
                                />
                                {isPickerLineOpen && (
                                    <div className={styles.picker}>
                                        <SketchPicker color={busLineColor} onChange={handleLineColorChange} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.sec}>
                        <TitleSubtitle
                            title={t("frequency")}
                            subtitle={t("indicateHereTheAverageBusFrequencyAtEachStop")}
                            layout="busTracking"
                        />
                        <NumberInput
                            placeholder={t("selectNumber")}
                            value={frequency || ""}
                            size="medium"
                            className={styles.freqInput}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFrequency(e.target.value);
                            }}
                        />
                    </div>

                    <div className={`${styles.sec} ${styles.secLast}`}>
                        <TitleSubtitle
                            title={t("affiliatedBuses")}
                            subtitle={t("oneBusOneTourInfo")}
                            layout="busTracking"
                        />
                        {affiliatedBuses && affiliatedBuses.length > 0 ? (
                            <>
                                <div className={styles.refreshBar}>
                                    {busesFetchedAt && (
                                        <span className={styles.refreshedAt}>
                                            {t("lastUpdated")} · {busesFetchedAt.toLocaleTimeString(i18n.language)}
                                        </span>
                                    )}
                                    <Button
                                        label={isRefreshingBuses ? t("refreshing") : t("refreshPositions")}
                                        onClick={handleRefreshBuses}
                                        disabled={isRefreshingBuses}
                                        variant="outline"
                                        isSmall
                                        icon={
                                            <RefreshIcon
                                                className={`${styles.refreshIcon} ${isRefreshingBuses ? styles.refreshIconSpinning : ""}`}
                                            />
                                        }
                                    />
                                </div>
                                <div className={styles.card}>
                                    <table className={styles.tbl}>
                                        <thead>
                                            <tr>
                                                <th>Bus Number</th>
                                                <th>Bus Immat</th>
                                                <th>Bus Brand</th>
                                                <th>Bus Model</th>
                                                <th>{t("lastPosition")}</th>
                                                <th className={styles.mapCol}>Display Map</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {affiliatedBuses.map((bus) => (
                                                <tr key={bus.id}>
                                                    <td className={styles.num}>{bus.busNumber}</td>
                                                    <td>{bus.busImmat}</td>
                                                    <td>{bus.busBrand}</td>
                                                    <td>{bus.busModel}</td>
                                                    <td>{renderLastPosition(bus)}</td>
                                                    <td className={styles.mapCol}>
                                                        <div className={styles.mapActs}>
                                                            <button
                                                                type="button"
                                                                className={styles.btnRed}
                                                                onClick={() => toggleDisplayMap(bus.id, bus.displayMap)}
                                                            >
                                                                {bus.displayMap ? t("hide") : t("show")}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={styles.btnRed}
                                                                onClick={() => handleUnaffiliate(bus.id)}
                                                            >
                                                                {t("unaffiliate")}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <p className={styles.empty}>{t("noAffiliatedBuses")}</p>
                        )}

                        <div className={styles.availTitle}>{t("availableBuses")}</div>
                        <div className={styles.availSub}>{t("busesYouCanLinkToThisTour")}</div>
                        {availableBuses && availableBuses.length > 0 ? (
                            <div className={styles.card}>
                                <table className={styles.tbl}>
                                    <thead>
                                        <tr>
                                            <th>Bus Number</th>
                                            <th>Bus Immat</th>
                                            <th>Bus Brand</th>
                                            <th>Bus Model</th>
                                            <th>{t("currentTour")}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {availableBuses.map((bus) => (
                                            <tr key={bus.id}>
                                                <td className={styles.num}>{bus.busNumber}</td>
                                                <td>{bus.busImmat}</td>
                                                <td>{bus.busBrand}</td>
                                                <td>{bus.busModel}</td>
                                                <td>{bus.currentTrackName || t("unassigned")}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className={styles.btnRed}
                                                        onClick={() => handleAffiliateClick(bus)}
                                                    >
                                                        {bus.currentTrackId ? t("transferHere") : t("affiliate")}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className={styles.empty}>{t("noAvailableBuses")}</p>
                        )}
                    </div>
                </>
            )}

            <div className={`${stepStyles.stepButton} ${stepStyles.footer} ${styles.footer}`}>
                <Button
                    label={t("continue")}
                    onClick={handleContinue}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>

            {busToTransfer && (
                <div className={studio.overlay}>
                    <div className={`${studio.content} ${studio.deleteContent}`}>
                        <h1 className={studio.title}>{t("confirmBusTransfer")}</h1>
                        <div className={studio.message}>
                            {t("busTransferMessage", {
                                track: busToTransfer.currentTrackName || t("unassigned"),
                            })}
                        </div>
                        <div className={studio.buttonsCentered}>
                            <Button variant="cancel" label={t("cancel")} onClick={() => setBusToTransfer(null)} />
                            <Button
                                variant="outline"
                                shape="pill"
                                label={t("transferHere")}
                                onClick={confirmTransfer}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusTracking;
